import React, { useRef, useState, useMemo, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Stars, Text, Float, Line, PerspectiveCamera, Environment, Sphere, MeshDistortMaterial, Points, PointMaterial, Plane, Grid } from '@react-three/drei';
import * as THREE from 'three';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Zap, Activity, Server, Database, Globe, Lock, Cpu, Clock, AlertTriangle, CheckCircle, Terminal, RefreshCw, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

// --- 3D COMPONENTS ---

const NeuralCore = ({ status }: { status: 'stable' | 'warning' | 'critical' }) => {
    const meshRef = useRef<THREE.Mesh>(null);
    const color = status === 'stable' ? '#22c55e' : status === 'warning' ? '#eab308' : '#ef4444';

    useFrame((state) => {
        if (meshRef.current) {
            meshRef.current.rotation.x = state.clock.getElapsedTime() * 0.2;
            meshRef.current.rotation.y = state.clock.getElapsedTime() * 0.3;
        }
    });

    return (
        <Float speed={2} rotationIntensity={0.5} floatIntensity={0.5}>
            <Sphere args={[1.5, 64, 64]} ref={meshRef}>
                <MeshDistortMaterial
                    color={color}
                    emissive={color}
                    emissiveIntensity={0.5}
                    distort={0.4}
                    speed={2}
                    wireframe={true}
                    transparent
                    opacity={0.8}
                />
            </Sphere>
            <Sphere args={[1.4, 32, 32]}>
                <meshBasicMaterial color={color} transparent opacity={0.15} />
            </Sphere>
            {/* Core Pulse Ring */}
            <mesh rotation={[Math.PI / 2, 0, 0]}>
                <ringGeometry args={[1.8, 1.85, 64]} />
                <meshBasicMaterial color={color} transparent opacity={0.3} side={THREE.DoubleSide} />
            </mesh>
        </Float>
    );
};

const ParticleField = () => {
    const ref = useRef<THREE.Points>(null);
    const count = 2000;
    const [positions] = useState(() => {
        const pos = new Float32Array(count * 3);
        for (let i = 0; i < count; i++) {
            const theta = THREE.MathUtils.randFloatSpread(360);
            const phi = THREE.MathUtils.randFloatSpread(360);
            // Create a swirling cloud
            pos[i * 3] = (Math.random() - 0.5) * 15;
            pos[i * 3 + 1] = (Math.random() - 0.5) * 5;
            pos[i * 3 + 2] = (Math.random() - 0.5) * 15;
        }
        return pos;
    });

    useFrame((state) => {
        if (ref.current) {
            ref.current.rotation.y = state.clock.getElapsedTime() * 0.05;
        }
    });

    return (
        <Points ref={ref} positions={positions} stride={3} frustumCulled={false}>
            <PointMaterial
                transparent
                color="#3b82f6"
                size={0.03}
                sizeAttenuation={true}
                depthWrite={false}
                opacity={0.6}
            />
        </Points>
    )
}

const Satellite = ({ position, label, icon: Icon, color = "#3b82f6", onClick, isSelected }: any) => {
    const groupRef = useRef<THREE.Group>(null);
    const meshRef = useRef<THREE.Mesh>(null);
    const [hovered, setHovered] = useState(false);

    // Scale effect on hover/select
    const targetScale = isSelected ? 1.5 : hovered ? 1.2 : 1;

    useFrame(({ clock }) => {
        if (groupRef.current && meshRef.current) {
            // Self-rotate
            meshRef.current.rotation.y += 0.01;
            // Lerp scale
            meshRef.current.scale.lerp(new THREE.Vector3(targetScale, targetScale, targetScale), 0.1);
        }
    });

    return (
        <Float speed={2} rotationIntensity={0.1} floatIntensity={0.5} position={position}>
            <group
                ref={groupRef}
                onClick={(e) => { e.stopPropagation(); onClick(); }}
                onPointerOver={() => { document.body.style.cursor = 'pointer'; setHovered(true); }}
                onPointerOut={() => { document.body.style.cursor = 'auto'; setHovered(false); }}
            >
                <Sphere args={[0.25, 32, 32]} ref={meshRef}>
                    <meshStandardMaterial
                        color={isSelected ? "#ffffff" : color}
                        emissive={color}
                        emissiveIntensity={isSelected ? 3 : 1.5}
                        roughness={0.2}
                        metalness={0.8}
                    />
                </Sphere>
                {/* Selection Halo */}
                {isSelected && (
                    <mesh>
                        <sphereGeometry args={[0.35, 32, 32]} />
                        <meshBasicMaterial color={color} transparent opacity={0.3} wireframe />
                    </mesh>
                )}
                <Text
                    position={[0, 0.5, 0]}
                    fontSize={hovered || isSelected ? 0.25 : 0.15}
                    color="white"
                    anchorX="center"
                    anchorY="middle"
                    fillOpacity={hovered || isSelected ? 1 : 0.7}
                >
                    {label}
                </Text>
                {/* Connection to Core */}
                <Line points={[[0, 0, 0], [-position[0], -position[1], -position[2]]]} color={color} opacity={isSelected ? 0.5 : 0.1} transparent lineWidth={1} />
            </group>
        </Float>
    )
}

const Scene = ({ systemStatus, onNodeSelect, selectedNode }: any) => {
    // Camera controls for "Cinematic" feel
    const { camera } = useThree();

    useFrame((state) => {
        // Slight camera sway
        const t = state.clock.getElapsedTime();
        camera.position.y = THREE.MathUtils.lerp(camera.position.y, Math.sin(t * 0.2) * 0.5, 0.01);
    });

    return (
        <>
            <PerspectiveCamera makeDefault position={[0, 1, 9]} fov={50} />
            <OrbitControls
                enablePan={false}
                enableZoom={true}
                minDistance={5}
                maxDistance={15}
                autoRotate={!selectedNode}
                autoRotateSpeed={0.5}
            />

            {/* Lighting */}
            <ambientLight intensity={0.2} />
            <pointLight position={[10, 10, 10]} intensity={1.5} color="#ffffff" />
            <pointLight position={[-10, -5, -10]} intensity={1} color="#3b82f6" />
            <spotLight position={[0, 10, 0]} intensity={1} angle={0.5} penumbra={1} color="#22c55e" />

            {/* Environment */}
            <Stars radius={100} depth={50} count={7000} factor={4} saturation={0} fade speed={1} />
            <ParticleField />

            {/* Grid Floor for Scale */}
            <Grid
                position={[0, -4, 0]}
                args={[20, 20]}
                cellSize={1}
                cellThickness={0.5}
                cellColor="#1e293b"
                sectionSize={5}
                sectionThickness={1}
                sectionColor="#3b82f6"
                fadeDistance={20}
                fadeStrength={1}
            />

            {/* The Core */}
            <NeuralCore status={systemStatus.overall === 'Operational' ? 'stable' : 'warning'} />

            {/* Interactive Satellites */}
            <Satellite position={[3.5, 1, 0]} label="API GATEWAY" color="#3b82f6" onClick={() => onNodeSelect('API')} isSelected={selectedNode === 'API'} />
            <Satellite position={[-3.5, 2, 1.5]} label="DATABASE" color="#10b981" onClick={() => onNodeSelect('DB')} isSelected={selectedNode === 'DB'} />
            <Satellite position={[2.5, -2, 2.5]} label="AUTH" color="#8b5cf6" onClick={() => onNodeSelect('AUTH')} isSelected={selectedNode === 'AUTH'} />
            <Satellite position={[-2.5, -1, -2.5]} label="CDN" color="#f59e0b" onClick={() => onNodeSelect('CDN')} isSelected={selectedNode === 'CDN'} />
            <Satellite position={[0, 3.5, -1]} label="PAYMENTS" color="#ec4899" onClick={() => onNodeSelect('PAY')} isSelected={selectedNode === 'PAY'} />
        </>
    );
};

// --- HUD & DIAGNOSTICS PANELS ---

const DiagnosticPanel = ({ node, onClose }: { node: string, onClose: () => void }) => {
    // Simulated Node Data
    const data = useMemo(() => {
        const base = {
            'API': { name: 'API Gateway Cluster', ver: 'v2.4.1-stable', uptime: '99.99%', cpu: 45, ram: 62 },
            'DB': { name: 'PostgreSQL Primary', ver: 'v15.4', uptime: '99.95%', cpu: 28, ram: 84 },
            'AUTH': { name: 'Auth Service (JWT)', ver: 'v1.2.0', uptime: '100.00%', cpu: 12, ram: 30 },
            'CDN': { name: 'Edge Delivery Network', ver: 'N/A', uptime: '100.00%', cpu: 5, ram: 10 },
            'PAY': { name: 'Payment Processor', ver: 'v3.0.1', uptime: '99.98%', cpu: 15, ram: 40 },
        }[node] || { name: 'Unknown Node', ver: 'N/A', uptime: '0%', cpu: 0, ram: 0 };
        return base;
    }, [node]);

    const [simCpu, setSimCpu] = useState(data.cpu);

    useEffect(() => {
        const i = setInterval(() => {
            setSimCpu(prev => Math.min(100, Math.max(0, prev + (Math.random() - 0.5) * 10)));
        }, 1000);
        return () => clearInterval(i);
    }, []);

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.9, x: 20 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.9, x: 20 }}
            className="absolute right-4 top-24 bottom-24 w-80 md:w-96 bg-black/80 backdrop-blur-xl border border-primary/30 rounded-2xl shadow-2xl overflow-hidden flex flex-col pointer-events-auto z-50"
        >
            <div className="p-4 border-b border-white/10 flex justify-between items-center bg-white/5">
                <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-primary" />
                    <span className="font-bold tracking-wider">{node}_DIAGNOSTICS</span>
                </div>
                <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full hover:bg-white/10" onClick={onClose}>
                    <X size={14} />
                </Button>
            </div>

            <div className="p-6 flex-1 overflow-y-auto space-y-6">
                <div>
                    <h3 className="text-xl font-bold text-white mb-1">{data.name}</h3>
                    <div className="flex gap-2">
                        <Badge variant="outline" className="border-green-500/30 text-green-400 bg-green-500/10">Connected</Badge>
                        <Badge variant="outline" className="border-white/20 text-muted-foreground">{data.ver}</Badge>
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="space-y-2">
                        <div className="flex justify-between text-xs text-muted-foreground">
                            <span>CPU LOAD</span>
                            <span>{Math.round(simCpu)}%</span>
                        </div>
                        <Progress value={simCpu} className="h-1.5 bg-white/10" indicatorClassName={simCpu > 80 ? 'bg-red-500' : 'bg-primary'} />
                    </div>
                    <div className="space-y-2">
                        <div className="flex justify-between text-xs text-muted-foreground">
                            <span>MEMORY USAGE</span>
                            <span>{data.ram}%</span>
                        </div>
                        <Progress value={data.ram} className="h-1.5 bg-white/10" indicatorClassName="bg-blue-400" />
                    </div>
                </div>

                <div className="p-4 bg-white/5 rounded-lg border border-white/10 space-y-2">
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Uptime (30d)</span>
                        <span className="font-mono">{data.uptime}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Last Restart</span>
                        <span className="font-mono text-xs">14d 2h ago</span>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <Button variant="outline" className="w-full border-white/10 hover:bg-white/5 gap-2">
                        <Terminal size={14} /> View Logs
                    </Button>
                    <Button variant="outline" className="w-full border-red-500/20 text-red-400 hover:bg-red-500/10 gap-2 hover:text-red-300">
                        <RefreshCw size={14} /> Restart
                    </Button>
                </div>
            </div>

            <div className="p-2 border-t border-white/10 bg-black/40 text-[10px] text-center text-muted-foreground font-mono uppercase">
                SECURE_CONNECTION // TLS 1.3 // AES-256
            </div>
        </motion.div>
    );
};

const HUDItem = ({ label, value, color = "text-primary", icon: Icon }: any) => (
    <div className="bg-black/40 backdrop-blur-md border border-white/10 p-3 rounded-lg flex flex-col min-w-[120px] shadow-lg">
        <div className="flex justify-between items-start mb-1">
            <span className="text-[10px] text-muted-foreground font-mono uppercase tracking-widest">{label}</span>
            {Icon && <Icon size={12} className="text-white/50" />}
        </div>
        <span className={`text-xl font-bold font-mono ${color}`}>{value}</span>
    </div>
)

const LogLine = ({ text, time, type }: any) => (
    <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className={`text-xs font-mono mb-1 flex gap-2 ${type === 'err' ? 'text-red-400' : 'text-green-500/80'}`}
    >
        <span className="opacity-50 text-white">[{time}]</span>
        <span>{text}</span>
    </motion.div>
)

const SystemStatusPage = () => {
    const [selectedNode, setSelectedNode] = useState<string | null>(null);

    // Simulation State
    const [logs, setLogs] = useState<any[]>([]);
    const [metrics, setMetrics] = useState({
        rps: 1240,
        latency: 42,
        errors: 0.01,
        active_threats: 0
    });

    useEffect(() => {
        const interval = setInterval(() => {
            // Update Metrics
            setMetrics(prev => ({
                rps: Math.floor(prev.rps + (Math.random() - 0.5) * 50),
                latency: Math.floor(prev.latency + (Math.random() - 0.5) * 5),
                errors: Math.max(0, prev.errors + (Math.random() - 0.5) * 0.001),
                active_threats: Math.random() > 0.95 ? 1 : 0
            }));

            // Add Logs
            const interactions = [
                { t: 'Packet verified via Node: LILONGWE-01', type: 'info' },
                { t: 'Secure handshake verified', type: 'info' },
                { t: 'DB connection pool balanced', type: 'info' },
                { t: 'Cache hit ratio: 98%', type: 'info' },
                { t: 'Automated threat scan confirmed: 0 threats', type: 'info' },
                { t: 'Latency optimization routine verified', type: 'info' },
                { t: 'WARN: High packet loss detected in Sector 7', type: 'err' }
            ];

            if (Math.random() > 0.3) {
                const item = interactions[Math.floor(Math.random() * interactions.length)];
                const newLog = {
                    text: item.t,
                    type: item.type,
                    time: new Date().toLocaleTimeString()
                };
                setLogs(prev => [newLog, ...prev].slice(0, 8));
            }

        }, 1500);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="w-full h-screen bg-[#050505] text-white relative overflow-hidden font-sans selection:bg-primary/30">
            {/* 3D Scene Background */}
            <div className="absolute inset-0 z-0 cursor-move">
                <Canvas shadows dpr={[1, 2]}>
                    <Scene
                        systemStatus={{ overall: 'Operational' }}
                        onNodeSelect={setSelectedNode}
                        selectedNode={selectedNode}
                    />
                </Canvas>
            </div>

            {/* Overlay Gradient */}
            <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_center,transparent_0%,#000000_120%)]" />

            {/* HUD Layer */}
            <div className="relative z-10 w-full h-full p-4 md:p-8 flex flex-col pointer-events-none">

                {/* Header */}
                <div className="flex justify-between items-start pointer-events-auto mb-8">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-green-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                            </div>
                            <h1 className="text-3xl font-bold tracking-widest text-white drop-shadow-lg">
                                NEURAL_CORE <span className="text-primary">//</span> SYSTEM_MONITOR
                            </h1>
                        </div>
                        <div className="flex items-center gap-4 text-xs font-mono text-muted-foreground">
                            <span className="flex items-center gap-1"><Shield size={10} /> ENCRYPTION: ACTIVE</span>
                            <span className="flex items-center gap-1"><Globe size={10} /> REGION: GLOBAL_MW</span>
                            <span className="flex items-center gap-1"><Clock size={10} /> {new Date().toLocaleTimeString()}</span>
                        </div>
                    </div>
                </div>

                {/* Metrics Bar */}
                <div className="absolute top-8 right-8 flex flex-col gap-3 pointer-events-auto">
                    <HUDItem label="REQ/SEC" value={metrics.rps} color="text-blue-400" icon={Zap} />
                    <HUDItem label="LATENCY" value={`${metrics.latency}ms`} color={metrics.latency < 50 ? "text-green-400" : "text-yellow-400"} icon={Activity} />
                    <HUDItem label="THREATS" value={metrics.active_threats} color={metrics.active_threats > 0 ? "text-red-500 animate-pulse" : "text-green-500"} icon={Shield} />
                </div>

                {/* Middle - Diagnostic Panel Area */}
                <AnimatePresence>
                    {selectedNode && <DiagnosticPanel node={selectedNode} onClose={() => setSelectedNode(null)} />}
                </AnimatePresence>

                {/* Footer Content */}
                <div className="mt-auto pointer-events-auto grid grid-cols-1 md:grid-cols-3 gap-6 items-end">

                    {/* System Log */}
                    <div className="md:col-span-1 bg-black/80 backdrop-blur-xl border border-white/10 rounded-xl p-4 h-56 flex flex-col shadow-2xl overflow-hidden relative">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-50" />
                        <div className="flex items-center justify-between mb-3 border-b border-white/10 pb-2">
                            <span className="text-xs font-bold text-muted-foreground flex items-center gap-2">
                                <Terminal size={12} /> LIVE_KERNEL_LOGS
                            </span>
                            <Badge variant="outline" className="text-[10px] h-4 border-green-500/30 text-green-500">STREAMING</Badge>
                        </div>
                        <div className="flex-1 overflow-hidden flex flex-col-reverse mask-image-b">
                            <AnimatePresence>
                                {logs.map((log, i) => (
                                    <LogLine key={i} {...log} />
                                ))}
                            </AnimatePresence>
                        </div>
                    </div>

                    {/* Quick Status Grid */}
                    <div className="md:col-span-2 flex justify-end gap-4">
                        <Card className="bg-black/60 backdrop-blur border-white/5 hover:bg-white/5 transition-colors w-40">
                            <CardContent className="p-4 flex flex-col items-center gap-2">
                                <div className="p-2 bg-green-500/10 rounded-full text-green-500"><CheckCircle size={18} /></div>
                                <span className="text-xs font-bold text-muted-foreground">ALL SYSTEMS GO</span>
                            </CardContent>
                        </Card>
                        <Card className="bg-black/60 backdrop-blur border-white/5 hover:bg-white/5 transition-colors w-40">
                            <CardContent className="p-4 flex flex-col items-center gap-2">
                                <div className="p-2 bg-blue-500/10 rounded-full text-blue-500"><Shield size={18} /></div>
                                <span className="text-xs font-bold text-muted-foreground">SOC 2 COMPLIANT</span>
                            </CardContent>
                        </Card>
                    </div>

                </div>

            </div>
        </div>
    );
};

export default SystemStatusPage;
